import React, {FC} from "react";
import {useGetAppSettingsQuery} from "../../generated/graphql";
import {URL_HOME, URL_LEGAL_NOTICE, URL_PRIVACY_POLICY} from "../../models/url";
import {setDefaultCurrency} from "../../helpers/renderAmount";

type CurrencyBootstrapProps = {
    children: React.ReactNode;
};

// Startseite, Impressum und Datenschutz kommen ohne API aus und zeigen kein Geld.
// Sie dürfen nie hinter einer hängenden Settings-Abfrage verschwinden — das Impressum
// muss auch dann erreichbar sein, wenn das Backend klemmt.
const PUBLIC_PATHS = [URL_HOME, URL_LEGAL_NOTICE, URL_PRIVACY_POLICY];

const isPublicPath = (): boolean => {
    const path = typeof window === "undefined" ? "" : window.location.pathname;
    const normalized = path.length > 1 ? path.replace(/\/+$/, "") : path;

    return PUBLIC_PATHS.indexOf(normalized) !== -1;
};

/**
 * Setzt die Mandanten-Währung als globalen Default für renderAmount.
 * Muss innerhalb von ApolloWrapper gerendert werden, da die Query den Apollo
 * Client benötigt. Analog zu TimezoneBootstrap.
 *
 * Bewusst dieselbe Query wie TimezoneBootstrap statt GetCurrencySetting: Apollo
 * läuft mit addTypename:false und kann deshalb nicht normalisieren. Ein zweiter
 * Query, der nur {currency} selektiert, würde das vollständige settings-Objekt
 * im Cache durch ein Teilobjekt ersetzen ("Cache data may be lost") und beide
 * Queries gegeneinander nachladen lassen. Derselbe Request wie eine Ebene höher,
 * Apollo fasst ihn zusammen.
 */
const CurrencyBootstrap: FC<CurrencyBootstrapProps> = ({children}) => {
    const {data, loading} = useGetAppSettingsQuery();
    const currency = data?.settings?.currency;

    // Bewusst im Render statt in einem Effekt: Der Effekt liefe erst NACH dem ersten
    // Malen der Kinder, die dann in Euro stehen blieben. Das Ziel ist kein React-State,
    // sondern eine Modul-Variable, und die Zuweisung ist idempotent.
    // Nur bei vorhandenem Wert: Nach einem cache.reset() (Logout, Mandantenwechsel)
    // ist data kurzzeitig undefined, und ein Setter-Aufruf damit würde den Mandanten
    // bis zum nächsten Ergebnis auf Euro zurückwerfen.
    if (currency) {
        setDefaultCurrency(currency);
    }

    // Auf Mandanten-Bildschirmen die Kinder erst nach der Antwort rendern. Beim
    // Nachladen überspringt React den Teilbaum, weil children dieselbe Element-Referenz
    // bleibt (Props-Identitäts-Bailout) — ein bereits gemalter Betrag würde sich also
    // nie korrigieren. Ein Fehler beendet das Warten ebenfalls, die App bleibt hier
    // nie hängen.
    if (loading && !isPublicPath()) {
        return null;
    }

    return <>{children}</>;
};

export default CurrencyBootstrap;
